import { useState, useEffect } from "react";
import { Table, OptionsDataType, ColDataType, RowDataType } from "@thrivetribe/mvf.ui.organisms.table";
import { TableFilterButtons } from "ui/molecules/TableFilterButtons";
import { FixtureStats } from "types/Legacy/FixtureStats";
import { ClubStats } from "types/Legacy/ClubStats";
import { Scorer } from "types/Legacy/Scorer";
import "./StatsFilter.scss";

export interface Pill {
  label: string;
  data: {
    tag:
      | "LeagueFixtureStats_WeightGoal"
      | "LeagueFixtureStats_Scale"
      | "LeagueFixtureStats_Pitch"
      | "TopScorerPerLeague";
    tableTitle: string;
    tableAriaLabel: string;
    tablePrefixClass: string;
  };
}

export const pills: Pill[] = [
  {
    label: "Weight & Goals",
    data: {
      tag: "LeagueFixtureStats_WeightGoal",
      tableTitle: "Weight & Goals",
      tableAriaLabel: "Weight & Goals",
      tablePrefixClass: "weight-goals",
    },
  },
  {
    label: "Scale",
    data: {
      tag: "LeagueFixtureStats_Scale",
      tableTitle: "Scale",
      tableAriaLabel: "Scale",
      tablePrefixClass: "scale",
    },
  },
  {
    label: "Pitch",
    data: {
      tag: "LeagueFixtureStats_Pitch",
      tableTitle: "Pitch",
      tableAriaLabel: "Pitch",
      tablePrefixClass: "pitch",
    },
  },
  {
    label: "Top Scorer",
    data: {
      tag: "TopScorerPerLeague",
      tableTitle: "Top Scorer",
      tableAriaLabel: "Top Scorer",
      tablePrefixClass: "top-scorer",
    },
  },
];

interface StatsFilterProps {
  stats: ClubStats | undefined;
}

export const StatsFilter = ({ stats }: StatsFilterProps) => {
  const [selectedPill, setSelectedPill] = useState<Pill>(pills[0]);
  const [columnData, setColumnData] = useState<ColDataType[]>([{ id: "", label: "" }]);
  const [rowData, setRowData] = useState<RowDataType[]>([{}]);

  useEffect(() => {
    const tag = selectedPill.data.tag;
    const tableData = stats?.[tag];
    if (tableData === undefined) return;

    if (
      tag === "LeagueFixtureStats_WeightGoal" ||
      tag === "LeagueFixtureStats_Scale" ||
      tag === "LeagueFixtureStats_Pitch"
    ) {
      const typedTableData = tableData as FixtureStats[];

      const colData: ColDataType[] = [
        { id: "id", label: "#", align: "center" },
        { id: "team", label: "Team" },
        { id: "p", label: "P", align: "center" },
        { id: "w", label: "W", align: "center" },
        { id: "d", label: "D", align: "center" },
        { id: "l", label: "L", align: "center" },
        { id: "f", label: "F", align: "center" },
        { id: "a", label: "A", align: "center" },
        { id: "gd", label: "GD", align: "center" },
        { id: "pts", label: "PTS", align: "center" },
      ];
      setColumnData(colData);

      interface CustomRowDataType extends RowDataType {
        team: string | JSX.Element;
      }
      const rowData: CustomRowDataType[] = typedTableData?.map((rowData, index) => {
        return {
          id: index + 1,
          team: rowData.TeamName,
          p: rowData.TeamTotalPlayed_P,
          w: rowData.TeamTotalWon_W,
          d: rowData.TeamTotalDraw_D,
          l: rowData.TeamTotalLoss_L,
          f: rowData.TeamTotalGoalsFor_GF,
          a: rowData.TeamTotalGoalsAgainst_GA,
          gd: rowData.TeamTotalGoalsDifference_GD,
          pts: rowData.TeamTotalPts_Pts,
          options: { hoverDisabled: true },
        };
      });
      if (rowData.length < 8) {
        const lengthDiff = 8 - rowData.length;
        for (let i = 0; i < lengthDiff; i++) {
          rowData.push({ team: <>&nbsp;</>, options: { renderCellsWhenUndefined: true, hoverDisabled: true } });
        }
      }
      setRowData(rowData);

      return;
    }

    if (tag === "TopScorerPerLeague") {
      const typedTableData = tableData as Scorer[];

      const colData: ColDataType[] = [
        { id: "id", label: "#", align: "center" },
        { id: "team", label: "Team" },
        { id: "player", label: "Player" },
        { id: "w", label: "W", align: "center" },
        { id: "p", label: "P", align: "center" },
      ];
      setColumnData(colData);

      interface CustomRowDataType extends RowDataType {
        team?: string | JSX.Element;
      }
      const rowData: CustomRowDataType[] = typedTableData?.map((rowData, index) => {
        return {
          id: index + 1,
          team: rowData.TeamName,
          player: rowData.PlayerName,
          w: rowData.WeightGoals,
          p: `${rowData.LossPercent}%`,
          options: { hoverDisabled: true },
        };
      });
      if (rowData.length < 8) {
        const lengthDiff = 8 - rowData.length;
        for (let i = 0; i < lengthDiff; i++) {
          rowData.push({ team: <>&nbsp;</>, options: { renderCellsWhenUndefined: true, hoverDisabled: true } });
        }
      }
      setRowData(rowData);

      return;
    }
  }, [stats, selectedPill]);

  const options: OptionsDataType = {
    tableAriaLabel: selectedPill.data.tableAriaLabel,
    tablePrefixClass: `filter-table ${selectedPill.data.tablePrefixClass}`,
    headerSticky: true,
    headerColTopOffset: 52,
    headerEl: <h2>{selectedPill.data.tableTitle}</h2>,
  };

  return (
    <>
      <TableFilterButtons updatePillData={setSelectedPill} selectedPill={selectedPill} />
      <article className="filter-table-container">
        <Table options={options} columns={columnData} rows={rowData} />
      </article>
    </>
  );
};
